import { createSlice, createAsyncThunk } from "@reduxjs/toolkit"
import { del, post } from "../../lib/request"
import { extractCreatedSocialAccount } from "../../lib/utils/entityCreateMappers"

function positiveInt(value) {
  const num = Number(value)
  return Number.isFinite(num) && num > 0 ? num : null
}

/** DELETE /api/v1/entity-social-network-accounts/{id} */
export const deleteEntitySocialNetworkAccount = createAsyncThunk(
  "entitySocialNetworkAccounts/delete",
  async (accountId, { rejectWithValue }) => {
    const id = positiveInt(accountId)
    if (!id) return rejectWithValue("Missing social account id")
    try {
      await del(`entity-social-network-accounts/${id}`)
      return { accountId: id }
    } catch (error) {
      return rejectWithValue(error?.message || "Failed to remove social channel")
    }
  },
)

/** POST /api/v1/entity-social-network-accounts/entities/{entityId} */
export const addEntitySocialNetworkAccount = createAsyncThunk(
  "entitySocialNetworkAccounts/add",
  async (
    { entityId, socialNetworkTypeId, socialNetworkId, socialNetworkUsername, socialNetworkUrl },
    { rejectWithValue },
  ) => {
    const entityNum = positiveInt(entityId)
    const typeId = positiveInt(socialNetworkTypeId)
    if (!entityNum) return rejectWithValue("Missing entity id")
    if (!typeId) return rejectWithValue("Select a channel")
    try {
      const response = await post(`entity-social-network-accounts/entities/${entityNum}`, {
        socialNetworkTypeId: typeId,
        socialNetworkId: socialNetworkId == null ? "" : String(socialNetworkId),
        socialNetworkUsername: String(socialNetworkUsername ?? "").trim(),
        socialNetworkUrl: String(socialNetworkUrl ?? "").trim(),
      })
      const created = extractCreatedSocialAccount(response)
      return {
        entityId: entityNum,
        accountId: created.accountId,
        socialNetworkId: created.socialNetworkId,
        socialNetworkUsername: created.username,
        socialNetworkUrl: created.url,
        response,
      }
    } catch (error) {
      return rejectWithValue(error?.message || "Failed to add social channel")
    }
  },
)

const initialState = {
  adding: false,
  deleting: false,
  error: null,
}

const entitySocialNetworkAccountsSlice = createSlice({
  name: "entitySocialNetworkAccounts",
  initialState,
  reducers: {
    clearEntitySocialNetworkAccounts: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(deleteEntitySocialNetworkAccount.pending, (state) => {
        state.deleting = true
        state.error = null
      })
      .addCase(deleteEntitySocialNetworkAccount.fulfilled, (state) => {
        state.deleting = false
        state.error = null
      })
      .addCase(deleteEntitySocialNetworkAccount.rejected, (state, action) => {
        state.deleting = false
        state.error = action.payload || "Failed to remove social channel"
      })
      .addCase(addEntitySocialNetworkAccount.pending, (state) => {
        state.adding = true
        state.error = null
      })
      .addCase(addEntitySocialNetworkAccount.fulfilled, (state) => {
        state.adding = false
        state.error = null
      })
      .addCase(addEntitySocialNetworkAccount.rejected, (state, action) => {
        state.adding = false
        state.error = action.payload || "Failed to add social channel"
      })
  },
})

export const { clearEntitySocialNetworkAccounts } = entitySocialNetworkAccountsSlice.actions
export default entitySocialNetworkAccountsSlice.reducer
